import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Label } from '@art-of-hospitality-solutions-llc/ui-kit/components/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import {
  toastSuccess,
  toastError,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import service from '@Service';
import type { OperationActionTriggerProps } from '@Constants/operations';
import { shiftReportKeys } from '@Hooks/useShiftReports';
import { useShiftReportRunStore } from './shiftReportRunStore';

const toIsoDate = (d: Date) => d.toISOString().split('T')[0];

export default function RunShiftReportTrigger({
  soulId,
  onSuccess,
}: OperationActionTriggerProps) {
  const [open, setOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const queryClient = useQueryClient();

  const today = useMemo(() => new Date(), []);
  const defaultEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + 7);
    return d;
  }, [today]);

  const [startDate, setStartDate] = useState(toIsoDate(today));
  const [endDate, setEndDate] = useState(toIsoDate(defaultEnd));

  const handleRun = async () => {
    if (!startDate || !endDate) {
      toastError('Pick a start and end date.');
      return;
    }
    if (endDate < startDate) {
      toastError('End date must be on or after start date.');
      return;
    }
    setRunning(true);
    try {
      await service.runSummarizeActivity({ startDate, endDate, force: true });
      toastSuccess('Shift report job started.');
      useShiftReportRunStore.getState().begin(soulId);
      queryClient.invalidateQueries({ queryKey: shiftReportKeys.forSoul(soulId) });
      setOpen(false);
      onSuccess();
    } catch (err: any) {
      toastError('Failed to start shift report job.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" disabled={running}>
          {running ? 'Starting…' : 'Run Shift Report'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="flex flex-col space-y-3">
          <div>
            <Heading level="h4">Shift Report Range</Heading>
            <p className="mt-1 text-xs text-zinc-500">
              Select a date range to include reservations from.
            </p>
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-shift-start">Start Date</Label>
            <Input
              id="desk-shift-start"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="desk-shift-end">End Date</Label>
            <Input
              id="desk-shift-end"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="flex justify-end space-x-2 pt-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={running}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleRun} loading={running}>
              {running ? 'Starting…' : 'Run'}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
